import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Switch } from "@/components/ui/switch";

const KEY = "aufbruchwerk-consent";
const EVENT = "aufbruchwerk:open-consent";

export const openConsentSettings = () => { window.dispatchEvent(new Event(EVENT)); };

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
};

export const ConsentBanner = () => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [stats, setStats] = useState(false);

  useEffect(() => {
    const saved = read();
    if (!saved) setOpen(true); else setStats(!!saved.statistics);
    const show = () => { setDetails(true); setOpen(true); };
    window.addEventListener(EVENT, show);
    return () => { window.removeEventListener(EVENT, show); };
  }, []);

  const save = (statistics) => {
    localStorage.setItem(KEY, JSON.stringify({ necessary: true, statistics, date: new Date().toISOString() }));
    setStats(statistics);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="consent-banner"
          role="dialog"
          aria-label="Datenschutz-Einstellungen"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-[45] mx-auto max-w-xl rounded-[2px] border border-[var(--ink)]/10 bg-[#F3F0EA]/95 p-6 shadow-[0_30px_80px_-20px_rgba(26,26,24,0.45)] backdrop-blur-xl sm:inset-x-auto sm:left-6 sm:bottom-6 sm:p-7"
          data-lenis-prevent
        >
          <p className="eyebrow">Datenschutz</p>
          <p className="font-serif-d mt-3 text-2xl leading-tight text-[var(--ink)]">Ihre Daten, Ihre Wahl.</p>
          <p className="mt-3 text-sm leading-relaxed text-[var(--ink-2)]">
            Wir nutzen nur technisch notwendige Speicherung. Schriftarten laden von unserem eigenen Server, es gibt kein Tracking. Mehr in der <Link to="/datenschutz" onClick={() => setOpen(false)} className="underline decoration-[var(--clay)] underline-offset-4" data-testid="consent-privacy-link">Datenschutzerklärung</Link>.
          </p>
          {details && (
            <div className="mt-5 space-y-3 border-t border-[var(--line)] pt-5" data-testid="consent-details">
              <div className="flex items-start justify-between gap-4">
                <div><p className="text-sm font-medium text-[var(--ink)]">Notwendig</p><p className="text-xs text-[var(--ink-2)]">Speichert Ihre Auswahl. Immer aktiv.</p></div>
                <Switch checked disabled aria-label="Notwendig" data-testid="consent-necessary-switch" />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div><p className="text-sm font-medium text-[var(--ink)]">Statistik</p><p className="text-xs text-[var(--ink-2)]">Anonyme Reichweitenmessung. Derzeit nicht im Einsatz, wird nur mit Ihrer Zustimmung aktiviert.</p></div>
                <Switch checked={stats} onCheckedChange={setStats} aria-label="Statistik" data-testid="consent-statistics-switch" />
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => save(true)} data-testid="consent-accept-all" className="rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm text-[var(--stone)] transition-[background-color] duration-300 hover:bg-[var(--clay)]">Alle akzeptieren</button>
            <button onClick={() => save(false)} data-testid="consent-necessary-only" className="rounded-full border border-[var(--ink)]/25 px-5 py-2.5 text-sm text-[var(--ink)] transition-[border-color] duration-300 hover:border-[var(--ink)]">Nur notwendige</button>
            {details ? (
              <button onClick={() => save(stats)} data-testid="consent-save-selection" className="px-3 py-2.5 text-sm text-[var(--ink)] underline decoration-[var(--clay)] underline-offset-4">Auswahl speichern</button>
            ) : (
              <button onClick={() => setDetails(true)} data-testid="consent-show-details" className="px-3 py-2.5 text-sm text-[var(--ink)] underline decoration-[var(--clay)] underline-offset-4">Einstellungen</button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
